import React, { useCallback, useEffect, useState } from 'react';
import { showKomboConnect } from '@kombo-api/connect';
import { activateConnection, getConnectLink, getSyncStatus } from '../../api/integrations';
import { postMessage } from '../helper/browserPlugins';
import useHirePortContext from '../../hooks/useHirePortContext';

interface SyncStatusCheckerProps {
    onSyncComplete?: () => void;
}

/**
 * Component that detects when an organization needs initial sync
 * and shows the Kombo modal to complete the process.
 */
const SyncStatusChecker: React.FC<SyncStatusCheckerProps> = ({ onSyncComplete }) => {
    const [isModalShown, setIsModalShown] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isLoadingStatus, setIsLoadingStatus] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [awaitingInitialSync, setAwaitingInitialSync] = useState(false);
    const [explicitToolFromUrl, setExplicitToolFromUrl] = useState<string | undefined>(undefined);
    const { atsPostMessageStrategy = 'plugin' } = useHirePortContext();

    useEffect(() => {
        let mounted = true;
        // Extract tool from URL path (e.g., /embed/recruitee/...)
        const pathParts = window.location.pathname.split('/');
        if (pathParts.length > 2 && pathParts[1] === 'embed') {
            const tool = pathParts[2];
            // TODO: Optionally validate 'tool' against a list of known/supported tools
            if (mounted) setExplicitToolFromUrl(tool);
        }

        setIsLoadingStatus(true);
        setError(null);
        getSyncStatus()
            .then((response) => {
                if (!mounted) return;
                setAwaitingInitialSync(response.awaiting_initial_sync);
            })
            .catch((err) => {
                if (!mounted) return;
                setError(err instanceof Error ? err.message : 'Error fetching sync status');
            })
            .finally(() => {
                if (mounted) setIsLoadingStatus(false);
            });
        return () => { mounted = false; };
    }, []);

    const showSyncModal = useCallback(async () => {
        if (!awaitingInitialSync || isModalShown || isProcessing) {
            if (!awaitingInitialSync && isLoadingStatus) {
                setIsLoadingStatus(false);
            }
            return;
        }

        setIsLoadingStatus(true);
        setIsProcessing(true);
        setIsModalShown(true);
        setError(null);

        try {
            const linkOptions: { tool?: string } = {};
            if (explicitToolFromUrl) {
                linkOptions.tool = explicitToolFromUrl;
            }
            const link = await getConnectLink(linkOptions);

            if (!link) {
                setError('Failed to get connection link');
                setIsModalShown(false);
                return;
            }

            const connectToken = await showKomboConnect(link);
            await activateConnection(connectToken);

            postMessage({ event: "UIActionComplete", action: "initial_sync" }, atsPostMessageStrategy);

            if (onSyncComplete) {
                onSyncComplete();
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error during sync');
            setIsModalShown(false);
        } finally {
            setIsProcessing(false);
            setIsLoadingStatus(false);
        }
    }, [
        awaitingInitialSync,
        isModalShown,
        isProcessing,
        onSyncComplete,
        atsPostMessageStrategy,
        isLoadingStatus,
        explicitToolFromUrl
    ]);

    useEffect(() => {
        if (awaitingInitialSync && !isModalShown && !isProcessing) {
            showSyncModal();
        }
    }, [awaitingInitialSync, isModalShown, isProcessing, showSyncModal]);

    if (isLoadingStatus && !error && !isProcessing) {
        return null;
    }

    if (error) {
        return (
            <div style={{
                position: 'fixed',
                bottom: '20px',
                right: '20px',
                background: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '4px',
                padding: '10px 15px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                zIndex: 1000
            }}>
                <p style={{ margin: 0, color: '#721c24' }}>
                    <strong>Sync Error:</strong> {error}
                </p>
                <button
                    style={{
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        marginTop: '5px',
                        cursor: 'pointer'
                    }}
                    onClick={() => {
                        showSyncModal();
                    }}
                >
                    Retry
                </button>
            </div>
        );
    }

    return null;
};

export default SyncStatusChecker; 